import { useState } from 'react';
import {
  Badge,
  Cel,
  Kaart,
  KaartKop,
  Leeg,
  PaginaKop,
  Plek,
  Rij,
  Schakelaar,
  RangBadge,
  SkeletTabel,
  SpelerCel,
  Tabel,
  VormReeks
} from '../components/ui';
import {
  START_RATING,
  alleSeizoenen,
  erelijst,
  huidigSeizoen,
  deeltPlek,
  plekken,
  ranglijst as maakRanglijst,
  standSleutel,
  seizoenLabel,
  seizoenSleutel,
  seizoensStand
} from '../lib/elo';
import { toonRating } from '../lib/format';
import { vorm } from '../lib/prestaties';
import { useLeague } from '../lib/queries';
import { usePoule } from '../lib/poule';
import type { Match } from '../lib/types';

type Bereik = 'altijd' | 'seizoen';

export default function Ranglijst() {
  const { pouleId } = usePoule();
  const { players, actief, matches, elo, zoekSpeler, laden } = useLeague(pouleId);
  const [bereik, setBereik] = useState<Bereik>('altijd');
  const [seizoen, setSeizoen] = useState(huidigSeizoen());
  const [toonGestopt, setToonGestopt] = useState(false);

  if (laden) {
    return (
      <>
        <PaginaKop titel="Ranglijst" onder="Alle potjes sinds het begin, doorgerekend met Elo." />
        <Kaart>
          <SkeletTabel rijen={8} kolommen={8} />
        </Kaart>
        <div className="mt-3.5">
          <Kaart>
            <KaartKop titel="Erelijst" />
            <SkeletTabel rijen={3} kolommen={4} />
          </Kaart>
        </div>
      </>
    );
  }

  const perSeizoen = bereik === 'seizoen';
  const rijen = perSeizoen
    ? seizoensStand(seizoen, matches, zoekSpeler)
    : maakRanglijst(toonGestopt ? players : actief, matches, elo.ratings);

  /* Even veel punten is dezelfde plek; de eerstvolgende slaat er dan een over. */
  const plaatsen = plekken(rijen, standSleutel);

  const lijstErelijst = erelijst(matches, zoekSpeler);

  return (
    <>
      <PaginaKop
        titel="Ranglijst"
        onder={
          perSeizoen
            ? 'Seizoensstand: elk kwartaal begint met een zachte reset — een kwart van je voorsprong op de start gaat mee.'
            : 'Alle potjes sinds het begin, doorgerekend met Elo.'
        }
      />

      <Kaart>
        <div className="flex flex-wrap items-center gap-3">
          <Schakelaar<Bereik>
            waarde={bereik}
            onKies={setBereik}
            opties={[
              { waarde: 'altijd', label: 'Aller tijden' },
              { waarde: 'seizoen', label: 'Per seizoen' }
            ]}
          />
          {perSeizoen ? (
            <select className="veld w-auto" value={seizoen} onChange={(e) => setSeizoen(e.target.value)}>
              {alleSeizoenen(matches).map((k) => (
                <option key={k} value={k}>
                  {seizoenLabel(k)}
                </option>
              ))}
            </select>
          ) : (
            <label className="ml-auto flex items-center gap-2 text-xs text-krijt-500">
              <input type="checkbox" checked={toonGestopt} onChange={(e) => setToonGestopt(e.target.checked)} />
              gestopte spelers tonen
            </label>
          )}
        </div>
      </Kaart>

      <div className="mt-3.5">
        <Kaart>
          {rijen.length ? (
            <Tabel
              koppen={[
                '#',
                'Speler',
                'Rating',
                'Rang',
                ...(perSeizoen ? ['Vanaf'] : []),
                'Potjes',
                'G–V',
                'Winst',
                'Saldo',
                'Vorm'
              ]}
            >
              {rijen.map((r, i) => (
                <Rij key={r.player.id}>
                  <Cel className="w-8">
                    <Plek nr={plaatsen[i]} gedeeld={deeltPlek(plaatsen, i)} />
                  </Cel>
                  <Cel>
                    <span className="flex items-center gap-2">
                      <SpelerCel speler={r.player} metBijnaam />
                      {r.player.isActive === false ? <Badge toon="uit">gestopt</Badge> : null}
                    </span>
                  </Cel>
                  <Cel nummer>
                    <strong>{toonRating(r.rating)}</strong>
                  </Cel>
                  <Cel>
                    <RangBadge rating={r.rating} klein />
                  </Cel>
                  {perSeizoen ? (
                    <Cel nummer>
                      <span className="text-krijt-500">{toonRating(r.basis ?? START_RATING)}</span>
                    </Cel>
                  ) : null}
                  <Cel nummer>{r.stats.gespeeld}</Cel>
                  <Cel nummer>
                    {r.stats.gewonnen}–{r.stats.verloren}
                  </Cel>
                  <Cel nummer>{Math.round(r.stats.winstPct)}%</Cel>
                  <Cel nummer>
                    {r.stats.saldo > 0 ? '+' : ''}
                    {r.stats.saldo}
                  </Cel>
                  <Cel>
                    <Vorm id={r.player.id} matches={matches} seizoen={perSeizoen ? seizoen : null} />
                  </Cel>
                </Rij>
              ))}
            </Tabel>
          ) : (
            <Leeg
              tekst={perSeizoen ? 'In dit kwartaal is nog niet gespeeld.' : 'Nog geen spelers.'}
              ikoon="ranglijst"
            />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop titel="Erelijst" hint="winnaars van afgeronde kwartalen" />
          {lijstErelijst.length ? (
            <Tabel koppen={['Seizoen', 'Winnaar', 'Rating', 'Deelnemers']}>
              {lijstErelijst.map((r) => (
                <Rij key={r.sleutel}>
                  <Cel>{seizoenLabel(r.sleutel)}</Cel>
                  <Cel>
                    <span className="flex items-center gap-2">
                      🏅 <SpelerCel speler={r.winnaar.player} />
                    </span>
                  </Cel>
                  <Cel nummer>{toonRating(r.winnaar.rating)}</Cel>
                  <Cel nummer>{r.deelnemers}</Cel>
                </Rij>
              ))}
            </Tabel>
          ) : (
            <Leeg tekst="Er is nog geen kwartaal afgerond." ikoon="medaille" />
          )}
        </Kaart>
      </div>
    </>
  );
}

/** Laatste vijf uitslagen als W en V, oudste links. */
function Vorm({ id, matches, seizoen }: { id: string; matches: Match[]; seizoen: string | null }) {
  const eigen = seizoen ? matches.filter((m) => seizoenSleutel(m.playedAt) === seizoen) : matches;
  return <VormReeks potjes={vorm(id, eigen)} />;
}
